import { cn, DatePicker as V2DatePicker } from '@foresightdata/web-common-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { plainDateToZonedDateTime } from '../../../util/date-utilts';
import { calendarShortcutToValueMap, shortcutsSx } from '../../DatePicker/Calendar';
import { IDateMeta } from '../../../data-models/field3.data-model';
import { formTextFieldInlineClassNames } from '../../../theme/component-styles';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { IBaseFieldProps } from './types';

/**
 * The MUI date picker has a limit of 100 years in the past / future by default, see:
 * https://github.com/mui/mui-x/issues/7072
 *
 * With the `useV2Components` flag on (FD-6114) this is web-common's DatePicker: the presets
 * become its `shortcuts`, `fullWidth` a `w-full`, and the form-inline variant the same
 * borderless look the TextField wrapper gets. The bounds are plain `YYYY-MM-DD` strings and parse
 * as local dates, like the value (`new Date(string)` would read them as UTC, a day west of UTC).
 * Both paths take the value as `defaultValue`: a form `reset()` does not repaint the field, as
 * before.
 */
export const FieldDate: FC<IBaseFieldProps<IDateMeta>> = ({ formField, formProps }) => {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();
  const { ref, onChange, onBlur, value } = formProps;
  const { disabled = false, fullWidth = false, variant, autoFocus = false } = formField ?? {};
  const { minimum, maximum, presets, gridEditor = false } = formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? `${formTextFieldInlineClassNames} form-date-picker` : '';
  const shortcutItems = presets?.map((preset) => ({
    label: preset,
    getValue: calendarShortcutToValueMap[preset],
  }));

  let dateValue = value;
  if (typeof value === 'string') {
    // '' means no date. Converted, it becomes an Invalid Date, which the picker paints red.
    dateValue = value ? plainDateToZonedDateTime(value) : null;
  }

  if (useV2Components) {
    return (
      <V2DatePicker
        ref={ref}
        defaultValue={dateValue ?? null}
        onValueChange={onChange}
        onBlur={gridEditor ? undefined : onBlur}
        disabled={disabled}
        minDate={minimum ? plainDateToZonedDateTime(minimum) : undefined}
        maxDate={maximum ? plainDateToZonedDateTime(maximum) : undefined}
        shortcuts={shortcutItems}
        placeholder={variant === 'form-inline' ? '' : undefined}
        autoFocus={autoFocus}
        aria-label={formField.label}
        className={cn(fullWidth && 'w-full', variant === 'form-inline' && 'form-inline-input')}
      />
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        defaultValue={dateValue}
        disabled={disabled}
        maxDate={maximum ? plainDateToZonedDateTime(maximum) : undefined}
        minDate={minimum ? plainDateToZonedDateTime(minimum) : undefined}
        onAccept={onChange}
        onChange={onChange}
        onClose={onBlur}
        views={['year', 'month', 'day']}
        slotProps={{
          shortcuts: {
            items: shortcutItems,
            sx: shortcutsSx,
          },
          textField: {
            className,
            onBlur: gridEditor ? undefined : onBlur,
            ref,
            placeholder: variant === 'form-inline' ? '' : undefined,
            fullWidth,
            // As in `FieldText`: the shared label carries no `htmlFor`, so without this the
            // picker's input has no accessible name.
            inputProps: { 'aria-label': formField.label },
          },
        }}
        autoFocus={autoFocus}
      />
    </LocalizationProvider>
  );
};
